import { ReactNode } from 'react'
import { KnowledgeDocumentListItem, SocialPlatform } from '@digital-twins/shared-types'

// Accepted components → pages import (precedent: other shared components read
// library helpers): the library type label is owned by the Knowledge Hub.
import { documentTypeLabel } from '@/pages/KnowledgeHubPage/libraryHelpers'
import { getApiBaseUrl } from '@/lib/runtime-env'

import { CitationVerification } from './citationVerification'

/**
 * The one source shape every citation surface renders: the inline pill, its
 * card, the drawer rows and the unplaced rows. Page-level adapters
 * (`pages/ThreadDetailPage/citationSources.ts`, the Analyst tab) build it
 * from their own citation types, so this module never knows about them.
 */
export type CitationSourceKind = 'web' | 'library' | 'social' | 'study' | 'room' | 'run' | 'audience' | 'dataset' | 'connector_record'

export type CitationDocType = 'report' | 'deck' | 'data' | 'video' | 'audio' | 'link' | 'document'

/**
 * What a citation shows as a picture. `url` is https only (web page image,
 * library cover or link thumbnail, study cover); `socialPath` is the
 * authenticated image proxy path of a social post, loaded as a blob.
 * Resolved in one place (`pages/ThreadDetailPage/citationImage.ts`).
 */
export type CitationImage =
  | { kind: 'url'; src: string; sourceUrl?: string | null; width?: number | null; height?: number | null }
  | { kind: 'socialPath'; path: string }

/** The one allowed authenticated image path. Tokens are opaque message-scoped identifiers. */
export function sourceImageTokens(value: unknown): [string, string, string] | null {
  if (typeof value !== 'string') return null
  const match = /^\/api\/knowledge\/threads\/([0-9a-f-]{36})\/messages\/([0-9a-f-]{36})\/source-images\/([0-9a-f-]{36})$/i.exec(value)
  return match ? [match[1], match[2], match[3]] : null
}

/**
 * The saved picture's address on the API host, for a plain `<img>`: the app and
 * API hosts are same-site, so the session cookie goes with it and no preflight
 * is sent. `/api` is the app-relative prefix the API base URL replaces.
 */
export function sourceImageUrl(path: string): string | null {
  const tokens = sourceImageTokens(path)
  if (!tokens) return null
  const [threadToken, messageToken, imageToken] = tokens
  return `${getApiBaseUrl()}/knowledge/threads/${threadToken}/messages/${messageToken}/source-images/${imageToken}`
}

/** Raw, as on the wire: `unit` is an engine code, formatted by `formatFigure`. */
export interface CitationFigure {
  label: string
  value: number
  unit: string
}

export interface CitationSource {
  /** Stable per source, used for React keys and grouping. */
  key: string
  /** The 1-based chip number (kept for testids and order). */
  displayIndex: number
  kind: CitationSourceKind
  /** Recorded study goal, supplied by the authorized study projection. */
  goalId?: string | null
  /** Full name, the card header. */
  name: string
  /** Pill label: domain, short title, @handle or study name. */
  shortName: string
  /** Web: the favicon host. */
  hostname?: string | null
  platform?: SocialPlatform | null
  docType?: CitationDocType | null
  /** 'p. 4', '02:13', exhibit title + ' · n=100', host · date. */
  location?: string | null
  /** Text the source itself provided, shown in the muted quote block. */
  quote?: string | null
  /** Calculation figures to show, already narrowed to the cited one when known. */
  figures?: Array<CitationFigure> | null
  /** Social: the post preview, injected by the page layer. */
  preview?: ReactNode | null
  /** The cited source's own picture; never a stock cover. */
  image?: CitationImage | null
  verification: CitationVerification
  /** Https record link. Rendered as an Open link; absent when the record has none. */
  href?: string | null
  onOpen: () => void
}

const DOC_TYPE_BY_LABEL: Record<string, CitationDocType> = {
  report: 'report',
  'simulation report': 'report',
  document: 'document',
  deck: 'deck',
  data: 'data',
  video: 'video',
  audio: 'audio',
  link: 'link',
}

/** Library document → icon family. An unknown facet reads as a document. */
export function citationDocType(doc: KnowledgeDocumentListItem | undefined): CitationDocType {
  if (!doc) {
    return 'document'
  }
  const label = documentTypeLabel(doc).trim().toLowerCase()
  return DOC_TYPE_BY_LABEL[label] ?? 'document'
}

/** Compact only the literal hostname prefix; never change link destinations or quoted text. */
export function compactCitationDomain(label: string): string {
  return label.replace(/^(https?:\/\/)?www\./i, '$1')
}

/** `nytimes.com`, never `www.nytimes.com`: the `www.` prefix names nothing. */
export function hostnameFromUrl(url: string): string | null {
  if (!url) {
    return null
  }
  try {
    return compactCitationDomain(new URL(url).hostname)
  } catch {
    return null
  }
}

/**
 * Hostname only, never the article path, so the request cannot be used as
 * an open redirect into a publisher URL. Returns null for anything that is
 * not a plain host.
 */
export function faviconUrlForHostname(hostname: string): string | null {
  if (!/^[a-z0-9.-]+$/i.test(hostname)) {
    return null
  }

  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(hostname)}&sz=32`
}

/**
 * True on a device with no hover (touch). jsdom has no `matchMedia`, and no
 * answer means a hover device. `(hover: none)` and `(hover: hover)` are
 * mutually exclusive in a real browser; requiring both answers guards against
 * a stub that answers every query the same way.
 */
export function isHoverlessDevice(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia('(hover: none)').matches && !window.matchMedia('(hover: hover)').matches
}

/**
 * The value as an image `src` when it is a plain `https://` URL, else null.
 * Every citation image passes through here: no `http:`, `data:` or
 * `javascript:` source ever reaches an `<img>`.
 */
export function httpsImageSrc(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  if (!trimmed.startsWith('https://') || /\s/.test(trimmed)) {
    return null
  }
  try {
    return new URL(trimmed).protocol === 'https:' ? trimmed : null
  } catch {
    return null
  }
}
